import React, { useCallback, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { OrgProvider } from './context/OrgContext';
import ProtectedRoute from './components/ProtectedRoute'; // 🚀 ПРОВЕРЬ ПУТЬ!
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import { OfflineBanner } from './components/OfflineBanner';
import axiosClient from './api/axiosClient';
import {
    isServerUnavailable,
    isServerUnreachable,
    reportNetworkFailure,
    reportNetworkSuccess,
    resetNetworkStatus,
    subscribeNetworkStatus,
} from './api/networkStatus';
import LoadingScreen from './components/LoadingScreen';
import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { useWebSocket } from './hooks/useWebSocket';
import { ErrorBoundary } from './components/ErrorBoundary'; 



const theme = createTheme({
    palette: {
        primary: { main: '#1976d2' },
        secondary: { main: '#dc004e' },
        background: { default: '#f4f7f6' },
    },
});

const AppContent: React.FC = () => {
    const { loading, user } = useAuth();
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    // Сервер доступен, пока не накопилось N сбоев подряд (см. api/networkStatus.ts):
    // один таймаут больше не превращает приложение в заглушку.
    const [isServerDown, setIsServerDown] = useState(isServerUnavailable());

    // 1. СНАЧАЛА ВСЕ ХУКИ (БЕЗ УСЛОВИЙ ВЫШЕ) 🚀
    // Одно соединение на приложение: отсюда же играет звук и уходит push.
    useWebSocket(undefined, user?.id);

    const navigate = useNavigate();

    // Клик по уведомлению о сообщении открывает нужный чат.
    useEffect(() => {
        const handleOpenChat = (event: Event) => {
            const conversationId = (event as CustomEvent<{ conversationId?: string }>).detail?.conversationId;
            if (conversationId) navigate(`/dashboard/chat/${conversationId}`);
        };
        window.addEventListener('orion:open-chat', handleOpenChat);
        return () => window.removeEventListener('orion:open-chat', handleOpenChat);
    }, [navigate]);


    //  ГВАРДИЯ: Пробуждение интерфейса при возврате в приложение (Fix заморозки скролла)
    useEffect(() => {
        const handleWakeUp = () => {
            if (document.visibilityState === 'visible') {
                // Принудительно проверяем онлайн и даём сети новый шанс:
                // счётчик сбоев сбрасывается без перезагрузки страницы.
                setIsOnline(navigator.onLine);
                resetNetworkStatus();
            }
        };

        window.addEventListener('focus', handleWakeUp);
        document.addEventListener('visibilitychange', handleWakeUp);
        
        return () => {
            window.removeEventListener('focus', handleWakeUp);
            document.removeEventListener('visibilitychange', handleWakeUp);
        };
    }, []);

    // КОНТРОЛЬ ОНЛАЙН СТАТУСА И СЕТИ
    useEffect(() => {
        const handleStatusChange = () => setIsOnline(navigator.onLine);
        window.addEventListener('online', handleStatusChange);
        window.addEventListener('offline', handleStatusChange);

        const interceptor = axiosClient.interceptors.response.use(
            response => {
                reportNetworkSuccess();
                return response;
            },
            error => {
                // 4xx — сервер ответил, значит он жив; сбой копим только
                // при отсутствии ответа и на 5xx.
                if (isServerUnreachable(error)) {
                    reportNetworkFailure();
                } else {
                    reportNetworkSuccess();
                }
                return Promise.reject(error);
            }
        );

        return () => {
            window.removeEventListener('online', handleStatusChange);
            window.removeEventListener('offline', handleStatusChange);
            axiosClient.interceptors.response.eject(interceptor);
        };
    }, []);

    // Подписка на изменения доступности сервера
    useEffect(() => subscribeNetworkStatus(() => setIsServerDown(isServerUnavailable())), []);

    // ОБНОВЛЕНИЕ ПРИ СМЕНЕ SERVICE WORKER
    useEffect(() => {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                window.location.reload();
            });
        }
    }, []);

    // «Обновить» на плашке: сбрасываем счётчик и перечитываем данные.
    // В офлайне перезагрузка бессмысленна — остаёмся на кэше.
    const handleRefresh = useCallback(() => {
        resetNetworkStatus();
        if (navigator.onLine) {
            window.location.reload();
        }
    }, []);

    // УСЛОВНЫЕ РЕНДЕРЫ (После всех хуков)
    if (loading && !user && window.location.pathname !== '/login') {
        return <LoadingScreen />;
    }

    // 3. ОСНОВНОЙ РЕНДЕР: роуты живут всегда, плашка лишь предупреждает о связи.
    return (
        <Box sx={{ pt: 'var(--orion-offline-banner-height, 0px)' }}>
            {(!isOnline || isServerDown) && <OfflineBanner onRefresh={handleRefresh} />}
            <Routes>
                {/* Публичные роуты без лишних условий */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Приватные роуты под защитой */}
                <Route path="/dashboard/*" element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                } />

                <Route path="/" element={<Navigate to="/login" replace />} />
            </Routes>
        </Box>
    );
};


const App: React.FC = () => {
    return (
        <ErrorBoundary> {/* 🛡️ Внешний контур защиты */}
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Router>
                    <AuthProvider>
                        <OrgProvider>
                            <AppContent />
                        </OrgProvider>
                    </AuthProvider>
                </Router>
            </ThemeProvider>
        </ErrorBoundary>
    );
};

export default App;